<script lang="ts">
  import { createContext } from 'reka-ui'
  import { Dialog, DialogScrollContent } from '@gat-ship/vue-components/dialog'
  import type { Component, VNode } from 'vue'
  import { ref } from 'vue'
  import { type GatButtonProps } from '@gat-ship/vue-components'
  import ConfirmDialog from './confirm-dialog.vue'
  import type { ConfirmDialogProps } from './confirm-dialog.vue'

  export type ConfirmDialogContextProps = ConfirmDialogProps & {
    onOkClick?: () => Promise<void>
    onCancelClick?: () => Promise<void>
    slots?: {
      default?: () => VNode | Component
      footer?: () => VNode | Component
    }
  }

  export interface ConfirmDialogContext {
    showConfirmDialog: (props: ConfirmDialogContextProps) => void
  }

  const [_useConfirmDialog, provideConfirmDialog] =
    createContext<ConfirmDialogContext>('app-alert')

  export function useConfirmDialog() {
    try {
      return _useConfirmDialog()
    } catch (error) {
      console.error('Alert dialog not provided')
      return {
        showConfirmDialog: () => {}
      }
    }
  }
</script>

<script setup lang="ts">
  defineOptions({
    inheritAttrs: false
  })
  const isOpen = ref(false)
  const _alert = ref<ConfirmDialogContextProps | undefined>(undefined)

  function showConfirmDialog(alert: ConfirmDialogProps) {
    _alert.value = alert
    isOpen.value = true
  }

  function onOkClick() {
    const clickFn = _alert.value?.onOkClick
    if (typeof clickFn === 'undefined') {
      console.warn('No onOkClick function found')
      return
    }
    isOpen.value = false
    clickFn().catch((error) => {
      throw error
    })
  }

  function onCancelClick() {
    const clickFn = _alert.value?.onCancelClick
    if (typeof clickFn === 'undefined') {
      isOpen.value = false
      return
    }
    clickFn().catch((error) => {
      throw error
    })
  }

  provideConfirmDialog({
    showConfirmDialog
  })
</script>

<template>
  <ConfirmDialog
    :open="isOpen"
    @update:open="isOpen = $event"
    v-bind="_alert"
    @click:ok="onOkClick"
    @click:cancel="onCancelClick"
  >
    <template v-if="_alert?.slots?.default" #default>
      <component :is="_alert?.slots?.default" />
    </template>
  </ConfirmDialog>
  <slot />
</template>
